import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { differenceInDays, parseISO } from 'date-fns';
import braze, { BRAZE_CUSTOM_EVENTS } from 'helpers/braze';
import { httpClient } from 'helpers/http';
import URLS from 'constants/urls';
import { reportError } from 'helpers/error-logging';
import Segment from 'helpers/segment';
import _debounce from 'lodash/debounce';
import { CAMPAIGN_TYPE_TO_CAMPAIGN_CATEGORY } from 'constants/tracking';
import { PUBLISHED_STATUS } from 'features/Schedules/Schedules.helpers';

const OPPORTUNITY_DEBOUNCE_LENGTH = 500;

export const trackPageView = properties => {
  Segment.event('Page view', properties);
};

export const trackOpportunity = (() => {
  const venueHandlers = {};
  const createVenueHandler = venueId => {
    const globalOpportunityIds = new Set();
    const opportunityIds = new Set();

    const send = _debounce(
      () => {
        const ids = Array.from(opportunityIds.values());
        opportunityIds.clear();

        httpClient
          .post(URLS.venue.opportunitiesDisplay(venueId), {
            opportunity_ids: ids,
          })
          .catch(reportError);
      },
      OPPORTUNITY_DEBOUNCE_LENGTH,
      {
        trailing: true,
      }
    );

    return (opportunityId, displayName) => {
      const key = `${opportunityId}-${displayName}`;
      if (!globalOpportunityIds.has(key)) {
        globalOpportunityIds.add(`${opportunityId}-${displayName}`);
        opportunityIds.add(opportunityId);
        send();
      }
    };
  };

  return (venueId, opportunityId, displayName) => {
    if (!venueHandlers[venueId]) {
      venueHandlers[venueId] = createVenueHandler(venueId);
    }
    venueHandlers[venueId](opportunityId, displayName);
  };
})();

export const withOpportunityShownTracking = WrappedComponent =>
  class OpportunityTracking extends Component {
    // This isn't on `state` because it doesn't affect the render cycle
    trackedDisplay = false;

    static displayName = `withOpportunityShownTracking(${WrappedComponent.displayName || WrappedComponent.name})`;

    static propTypes = {
      visible: PropTypes.bool,
      opportunity: PropTypes.shape({
        scheduleId: PropTypes.number,
        id: PropTypes.number,
        venueId: PropTypes.number,
      }),
    };

    static defaultProps = {
      visible: true,
    };

    constructor(props) {
      super(props);
      this.trackOpportunityShown = this.trackOpportunityShown.bind(this);
    }

    componentDidMount() {
      const { visible } = this.props;
      if (visible) {
        this.trackOpportunityShown();
      }
    }

    componentDidUpdate(prevProps) {
      const { visible } = this.props;
      if (!prevProps.visible && visible) {
        this.trackOpportunityShown();
      }
    }

    trackOpportunityShown() {
      const { trackedDisplay } = this;
      const { opportunity } = this.props;

      if (!trackedDisplay && opportunity) {
        const { venueId, id: opportunityId } = opportunity;
        this.trackedDisplay = true;
        trackOpportunity(venueId, opportunityId, OpportunityTracking.displayName);
      }
    }

    render() {
      return <WrappedComponent trackOpportunityShown={this.trackOpportunityShown} {...this.props} />;
    }
  };

/*
  We forward Segment events to GA.

  The first arg passed to Segment.event is the event name.

  The second arg are "properties" that can be passed with the event.
    - https://segment.com/docs/spec/track/#properties

  Forwarding to GA requires specific "properties" to ensure the correct GA event shape.
    - https://segment.com/docs/destinations/google-analytics/#track

  The Segment event name is forwarded to GA as the event "action".

  To maintain consistency with existing Segment events and to correct forwarded events,
  some properties may appear multiple times under different keys.
*/

export const trackAcceptedOpportunity = (source, scheduleId, spotsAdded) =>
  Segment.event('Accept recommendation', {
    // ga
    category: 'Recommendations',
    label: source,
    // segment
    source,
    scheduleId,
    spotsAdded,
  });

export const trackAllocationChange = (source, scheduleId, allocation, change) =>
  Segment.event('Spot allocation changed', {
    // ga
    category: 'Classes',
    // segment
    source,
    scheduleId,
    allocation,
    change,
  });

export const trackAppointmentCancellation = ({ cancelReason, cancelReasonDescription }) =>
  Segment.event('Appointment cancellation', {
    // ga
    category: 'Appointments',
    label: `Cancellation reason: ${cancelReason || cancelReasonDescription}.`,
  });

export const trackEnabledClass = (source, scheduleId, recurrenceLevel, networkCapRecurrence, networkCap) =>
  Segment.event('Class enabled', {
    // ga
    category: 'Classes',
    label: source,
    // segment
    source,
    scheduleId,
    recurrenceLevel,
    networkCapRecurrence,
    networkCap,
  });

export const trackHomeOpportunityClicked = (venueId, dashboardUserId, recommendationName, shareType) =>
  Segment.event('Click Home Recommendation', {
    // ga
    category: 'Home Recommendations',
    // segment
    venueId,
    dashboardUserId,
    recommendationName,
    ...(shareType && { shareType }),
  });

export const trackHomeOpportunityViewed = (venueId, dashboardUserId, recommendationName) =>
  Segment.event('View Home Recommendation', {
    // ga
    category: 'Home Recommendations',
    // segment
    venueId,
    dashboardUserId,
    recommendationName,
  });

export const trackMonthlyReportDownload = ({ userId, venueId, year, month }) => {
  braze.trackEvent(BRAZE_CUSTOM_EVENTS.MONTHLY_REPORT_DOWNLOADED);
  return Segment.event('Monthly report download', {
    // ga
    category: 'Reports',
    label: `${userId}-${venueId}-${month}-${year}`,
  });
};

export const trackScheduleReservationsStopped = ({ reservationsStopped, scheduleId }) =>
  Segment.event('Toggle class full', {
    // ga
    category: 'Classes',
    label: `${scheduleId} - ${reservationsStopped ? 'Full' : 'Not full'}`,
  });

export const trackScheduleReservationsStoppedError = ({ scheduleId, error }) => {
  const { response = {} } = error;
  let { data = '' } = response;
  data = JSON.stringify(data);

  return Segment.event('Error - Toggle class full', {
    // ga
    category: 'Classes',
    label: `${scheduleId} - API response: ${data}`,
  });
};

export const trackUnsupportedSchedulingPlatformSelection = ({ platformName }) =>
  Segment.event('Calendar Sync - unsupported scheduling platform selected', {
    // ga
    category: 'Calendar Sync',
    label: `Unsupported scheduling platform: '${platformName}' was selected by a user`,
  });

// Amplitude events

export const trackSmartspotClassLevelChange = ({ isEnabled, classId, scheduleId }) =>
  Segment.event('smartspot class update', {
    'smartspot updated to': isEnabled ? 'ON' : 'OFF',
    'class id': classId,
    scheduleId,
  });

export const trackSmartspotVenueLevelChange = ({ isEnabled, source }) =>
  Segment.event('smartspot venue update', {
    'smartspot updated to': isEnabled ? 'ON' : 'OFF',
    'location updated': source,
  });

export const trackOpportunityAccepted = ({ source, scheduleId }) =>
  Segment.event('opportunities accept', { 'page location': source, 'schedule id': scheduleId });

export const trackCampaignLaunch = ({ type, startDate, endDate, promotions }) =>
  Segment.event(
    'campaign launch',
    {
      'campaign type': CAMPAIGN_TYPE_TO_CAMPAIGN_CATEGORY[type],
      'campaign duration': endDate ? differenceInDays(parseISO(endDate), parseISO(startDate)) : null,
      'active campaign count': promotions.filter(({ hasEnded }) => !hasEnded)?.length,
      'total campaign count': promotions.length,
    },
    {}
  );

export const trackPackageCreate = ({ packQuantity, retailPrice, venueIds }) =>
  Segment.event('package create', {
    'pack quantity': packQuantity,
    'retail price': retailPrice,
    'venue ids': venueIds,
    'venue count': venueIds?.length,
  });

export const trackClassScheduleAdd = ({ scheduleType, classId, instructorId, status, capacity }) =>
  Segment.event('class schedule add', {
    'class type': scheduleType,
    'class id': classId,
    'staff id': instructorId,
    'class published': status === PUBLISHED_STATUS,
    capacity,
  });

export const trackClassStatusChange = ({ hasEnabled, source, classId }) =>
  Segment.event('class status change', { 'has enabled': hasEnabled, location: source, 'class id': classId });

export const trackAddStaffBlock = ({ totalCount, practitionerId }) =>
  Segment.event(`block added`, { 'block type': 'staff', 'total count': totalCount, 'practitioner id': practitionerId });

export const trackAccessedCampaignsPage = () => Segment.event('User Accessed Campaigns Page', {}, {});
export const trackAccessedCreateCampaignPage = () => Segment.event('User Accessed Create Campaign Page', {}, {});

// Instructor tracking events
export const trackInstructorCreated = ({ instructorId, venueId, instructorName, email }) =>
  Segment.event('Instructor Created', {
    'instructor id': instructorId,
    'venue id': venueId,
    'instructor name': instructorName,
    email,
  });

export const trackInstructorUpdated = ({ instructorId, venueId, instructorName, email, changes }) =>
  Segment.event('Instructor Updated', {
    'instructor id': instructorId,
    'venue id': venueId,
    'instructor name': instructorName,
    email,
    changes,
  });

export const trackInstructorDeleted = ({ instructorId, venueId, instructorName, email }) =>
  Segment.event('Instructor Deleted', {
    'instructor id': instructorId,
    'venue id': venueId,
    'instructor name': instructorName,
    email,
  });

export const trackCrashedPageViewed = ({ venueId, errorCode }) =>
  Segment.event('Crashed page viewed', {
    venueId,
    errorCode,
  });
